/**
 * Dépôt d'un bon signé dans le compte du client, avec file d'attente hors ligne.
 *
 * Le bon est déposé en plus de l'email : le client garde sa preuve immédiate,
 * et le retrouve dans son espace. Si le réseau manque au moment de signer — un
 * parking souterrain, une zone blanche —, le bon est gardé sur le téléphone et
 * repart tout seul plus tard. L'agent n'est jamais bloqué devant le client.
 */
import { tokenStorage } from '../../../utils/token-storage';

const CLE_FILE = 'bons_en_attente';

export interface BonAEnvoyer {
  ref: string;
  clientId: number;
  day: string;
  signedAt: string;
  signerName: string;
  signerFunction: string;
  agentName: string;
  clientAbsent: boolean;
  gps?: { lat: number; lng: number; accuracy?: number };
  content: {
    prestations: { vehicule: string; type?: string; details: string }[];
    attestations: string[];
    /** Motif si le client signe « avec réserve » (litige tracé). */
    reserve?: string;
  };
  /** PNG de la signature, en base64 (data URL sans le préfixe). Absent si client absent. */
  signatureBase64?: string;
}

function base64VersBlob(base64: string): Blob {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) {
    octets[i] = binaire.charCodeAt(i);
  }
  return new Blob([octets], { type: 'image/png' });
}

async function envoyer(bon: BonAEnvoyer): Promise<boolean> {
  const base = process.env.REACT_APP_BACKEND_HOST || '';
  const token = tokenStorage.getToken();
  const form = new FormData();
  if (bon.signatureBase64) {
    form.append('signature', base64VersBlob(bon.signatureBase64), `${bon.ref}.png`);
  }
  form.append('ref', bon.ref);
  form.append('client_id', String(bon.clientId));
  form.append('day', bon.day);
  form.append('signed_at', bon.signedAt);
  form.append('signer_name', bon.signerName);
  form.append('signer_function', bon.signerFunction);
  form.append('agent_name', bon.agentName);
  form.append('client_absent', String(bon.clientAbsent));
  if (bon.gps) {
    form.append('gps_lat', String(bon.gps.lat));
    form.append('gps_lng', String(bon.gps.lng));
    if (bon.gps.accuracy !== undefined) {
      form.append('gps_accuracy', String(bon.gps.accuracy));
    }
  }
  form.append('content', JSON.stringify(bon.content));

  try {
    const res = await fetch(`${base}/api2/bons`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    // Le dépôt est idempotent côté serveur : un 409 éventuel signifie que le bon
    // est déjà là, donc traité. Seules les vraies pannes justifient un réessai.
    return res.ok || res.status === 409;
  } catch {
    return false;
  }
}

function lireFile(): BonAEnvoyer[] {
  try {
    return JSON.parse(localStorage.getItem(CLE_FILE) || '[]') as BonAEnvoyer[];
  } catch {
    return [];
  }
}

function ecrireFile(file: BonAEnvoyer[]): void {
  localStorage.setItem(CLE_FILE, JSON.stringify(file));
}

/**
 * Tente le dépôt ; en cas d'échec réseau, met le bon en file pour plus tard.
 * Retourne `true` si le bon est parti tout de suite, `false` s'il est en attente.
 */
export async function deposerBon(bon: BonAEnvoyer): Promise<boolean> {
  if (await envoyer(bon)) {
    return true;
  }
  const file = lireFile().filter((b) => b.ref !== bon.ref);
  file.push(bon);
  ecrireFile(file);
  return false;
}

/** Rejoue la file d'attente. Appelée au démarrage et au retour du réseau. */
export async function viderFileBons(): Promise<void> {
  const file = lireFile();
  if (!file.length) {
    return;
  }
  const restants: BonAEnvoyer[] = [];
  for (const bon of file) {
    if (!(await envoyer(bon))) {
      restants.push(bon);
    }
  }
  ecrireFile(restants);
}

export function nombreBonsEnAttente(): number {
  return lireFile().length;
}
